import { toast } from 'sonner'
import { Video } from 'lucide-react'
import { paths } from '@/routes/paths'
import { Button } from '@/components/ui/button'
import { useState, useEffect, useRef } from 'react'
import { useParams } from '@/routes/hooks/use-params'
import { useRouter } from '@/routes/hooks/use-router'
import axiosInstance, { endpoints } from '@/lib/axios'
import { ScrollArea } from '@/components/ui/scroll-area'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import CustomPagination from '@/components/pagination/custom-pagination'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'

import type { AdminClip, ClipFilters } from '../clip.types'
import type { ClipCategory } from '../../clip-categories/clip-category.types'

import ClipForm from '../clip-form'
import ClipsTable from '../clips-table'
import ClipsFilter from '../clips-filter'
import ClipViewDialog from '../clip-view-dialog'

// ----------------------------------------------------------------------

const PAGE_SIZE = 10

export default function ClipsView() {
  const router = useRouter()
  const params = useParams()
  const [clips, setClips] = useState<AdminClip[]>([])
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState<ClipFilters>({
    page: 1,
    limit: PAGE_SIZE,
    category: '',
    status: '',
    isPublished: 'all',
    search: '',
  })
  const [categories, setCategories] = useState<ClipCategory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [editClip, setEditClip] = useState<AdminClip | null>(null)
  const [editLoading, setEditLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  // True while a ClipForm R2 upload is in flight; locks the dialog so a refresh /
  // ESC / outside-click / X cannot drop a multi-minute upload. The only exit is
  // ClipForm's explicit "ยกเลิกการอัพโหลด" → confirm → abort.
  const [isUploading, setIsUploading] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    clip: AdminClip | null
  }>({ open: false, clip: null })
  const [viewClip, setViewClip] = useState<AdminClip | null>(null)

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchClips = async () => {
    setLoadError(false)
    try {
      const params: Record<string, string | number> = {
        page: filters.page,
        limit: filters.limit,
      }
      if (filters.category) params.category = filters.category
      if (filters.status) params.status = filters.status
      if (filters.isPublished !== 'all') params.isPublished = filters.isPublished
      const search = filters.search.trim()
      if (search) params.search = search

      const res = await axiosInstance.get(endpoints.clips.list, { params })
      const items: AdminClip[] = res.data?.items ?? res.data ?? []
      setClips(items)
      setTotal(res.data?.total ?? items.length)
      return items
    } catch (err) {
      console.error('fetchClips error', err)
      toast.error('โหลดคลิปล้มเหลว')
      setLoadError(true)
      return []
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get(endpoints.clipCategories.list)
      setCategories(res.data ?? [])
    } catch (err) {
      console.error('fetch clip categories error', err)
    }
  }

  const startPolling = (items: AdminClip[]) => {
    const hasProcessing = items.some((c) => c.status === 'processing')
    if (!hasProcessing) {
      stopPolling()
      return
    }
    if (pollingRef.current) return
    pollingRef.current = setInterval(async () => {
      const updated = await fetchClips()
      const stillProcessing = updated.some((c) => c.status === 'processing')
      if (!stillProcessing) stopPolling()
    }, 5000)
  }

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    fetchClips().then(startPolling)
    return stopPolling
  }, [filters])

  useEffect(() => {
    if (!params.id) {
      setEditClip(null)
      setEditLoading(false)
      return
    }

    const existingClip = clips.find((clip) => clip.id === params.id)
    if (existingClip) {
      setEditClip(existingClip)
      setEditLoading(false)
      return
    }

    let cancelled = false
    setEditLoading(true)
    axiosInstance
      .get<AdminClip>(endpoints.clips.detail(params.id))
      .then((res) => {
        if (!cancelled) setEditClip(res.data)
      })
      .catch((err) => {
        console.error('fetch clip detail error', err)
        if (!cancelled) {
          toast.error('โหลดข้อมูลคลิปล้มเหลว')
          router.replace(paths.clips)
        }
      })
      .finally(() => {
        if (!cancelled) setEditLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [params.id, clips])

  const handleEdit = (clip: AdminClip) => {
    router.push(paths.clipEdit(clip.id))
  }

  const handleCloseEditDialog = () => {
    router.push(paths.clips)
  }

  const handleEditSuccess = async () => {
    handleCloseEditDialog()
    const updated = await fetchClips()
    startPolling(updated)
  }

  const handleCreateSuccess = async () => {
    setCreateDialogOpen(false)
    const updated = await fetchClips()
    startPolling(updated)
  }

  const handleDelete = (clip: AdminClip) => {
    setConfirmDialog({ open: true, clip })
  }

  const handleConfirmDelete = async () => {
    if (!confirmDialog.clip || deleteLoading) return
    setDeleteLoading(true)
    try {
      await axiosInstance.delete(endpoints.clips.delete(confirmDialog.clip.id))
      setClips((prev) => prev.filter((c) => c.id !== confirmDialog.clip!.id))
      setTotal((t) => Math.max(0, t - 1))
      toast.success('ลบคลิปแล้ว')
    } catch (err) {
      console.error('delete clip error', err)
      toast.error('ลบคลิปล้มเหลว')
    } finally {
      setDeleteLoading(false)
      setConfirmDialog({ open: false, clip: null })
    }
  }

  const handleTogglePublish = async (clip: AdminClip, isPublished: boolean) => {
    try {
      await axiosInstance.patch(endpoints.clips.updateStatus(clip.id), {
        isPublished,
      })
      setClips((prev) =>
        prev.map((c) => (c.id === clip.id ? { ...c, isPublished } : c)),
      )
      toast.success(isPublished ? 'เผยแพร่คลิปแล้ว' : 'ซ่อนคลิปแล้ว')
    } catch (err) {
      console.error('toggle publish error', err)
      toast.error('อัปเดตสถานะล้มเหลว')
    }
  }

  const handleRetry = async (clip: AdminClip) => {
    try {
      await axiosInstance.post(endpoints.clips.retryTranscode(clip.id))
      // Optimistically flip to processing at the PARENT (not table-local) so the
      // row badge updates AND startPolling — which only arms while a clip is
      // processing — re-arms the 5s poll to pick up the result.
      const updated = clips.map((c) =>
        c.id === clip.id ? { ...c, status: 'processing' as const } : c,
      )
      setClips(updated)
      startPolling(updated)
      toast.success('สั่ง transcode ใหม่แล้ว')
    } catch (err) {
      console.error('retry transcode error', err)
      toast.error('สั่ง transcode ใหม่ล้มเหลว')
    }
  }

  const handleRowClick = (clip: AdminClip) => {
    setViewClip(clip)
  }

  return (
    <div className="w-full space-y-6">
      <ClipsFilter
        filters={filters}
        total={total}
        categories={categories}
        onFiltersChange={setFilters}
        onAddClick={() => setCreateDialogOpen(true)}
      />

      {loadError ? (
        <div className="border rounded-lg p-10 text-center space-y-3">
          <Video className="mx-auto h-10 w-10 text-gray-300" />
          <p className="text-sm text-muted-foreground">โหลดข้อมูลล้มเหลว</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setIsLoading(true)
              fetchClips().then(startPolling)
            }}
          >
            ลองใหม่
          </Button>
        </div>
      ) : (
        <ClipsTable
          clips={clips}
          categories={categories}
          loading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRetry={handleRetry}
          onRowClick={handleRowClick}
          onTogglePublish={handleTogglePublish}
        />
      )}

      {Math.ceil(total / PAGE_SIZE) > 1 && (
        <CustomPagination
          currentPage={filters.page}
          totalPages={Math.ceil(total / PAGE_SIZE)}
          onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
        />
      )}

      <ClipViewDialog
        clip={viewClip}
        categoryLabel={
          categories.find((c) => c.id === viewClip?.category)?.label
        }
        open={Boolean(viewClip)}
        onOpenChange={(open) => {
          if (!open) setViewClip(null)
        }}
        onEdit={(clip) => {
          setViewClip(null)
          handleEdit(clip)
        }}
        onRetry={(clip) => {
          setViewClip(null)
          handleRetry(clip)
        }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ open, clip: null })}
        title="ยืนยันการลบ"
        description={`คุณต้องการลบคลิป "${confirmDialog.clip?.title}" หรือไม่? การดำเนินการนี้ไม่สามารถยกเลิกได้`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />

      <Dialog
        open={createDialogOpen}
        onOpenChange={(open) => {
          // Ignore close requests while an upload is in flight; the only exit is
          // ClipForm's explicit cancel → confirm → abort.
          if (isUploading) return
          setCreateDialogOpen(open)
        }}
      >
        <DialogContent
          showCloseButton={!isUploading}
          onEscapeKeyDown={(e) => {
            if (isUploading) e.preventDefault()
          }}
          onInteractOutside={(e) => {
            if (isUploading) e.preventDefault()
          }}
          className="max-h-[calc(100dvh-2rem)] gap-0 overflow-hidden p-0 sm:max-w-3xl"
        >
          <ScrollArea className="max-h-[calc(100dvh-2rem)]">
            <div className="p-4 pt-10 sm:p-5 sm:pt-10">
              <DialogTitle className="sr-only">เพิ่มคลิป</DialogTitle>
              <ClipForm
                mode="create"
                variant="dialog"
                onCancel={() => setCreateDialogOpen(false)}
                onSuccess={handleCreateSuccess}
                onUploadingChange={setIsUploading}
              />
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(params.id)}
        onOpenChange={(open) => {
          // Edit mode does not upload today, but keep the lock symmetric in case
          // an upload path is ever added to edit.
          if (isUploading) return
          if (!open) handleCloseEditDialog()
        }}
      >
        <DialogContent
          showCloseButton={!isUploading}
          onEscapeKeyDown={(e) => {
            if (isUploading) e.preventDefault()
          }}
          onInteractOutside={(e) => {
            if (isUploading) e.preventDefault()
          }}
          className="max-h-[calc(100dvh-2rem)] gap-0 overflow-hidden p-0 sm:max-w-3xl"
        >
          <ScrollArea className="max-h-[calc(100dvh-2rem)]">
            <div className="p-4 pt-10 sm:p-5 sm:pt-10">
              <DialogTitle className="sr-only">แก้ไขคลิป</DialogTitle>
              {editLoading || !editClip ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  กำลังโหลดข้อมูลคลิป...
                </div>
              ) : (
                <ClipForm
                  clip={editClip}
                  mode="edit"
                  variant="dialog"
                  onCancel={handleCloseEditDialog}
                  onSuccess={handleEditSuccess}
                  onUploadingChange={setIsUploading}
                />
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  )
}
